"use client";

import React from "react";
import { motion } from "framer-motion";
import { TriangleAlert as AlertTriangle, Droplets, ShieldCheck, Waves, ArrowRight, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
const categories = [{
  title: "Emergency Repairs",
  description: "Burst pipes, leak detection, toilet overflow, and drain clogs.",
  icon: AlertTriangle
}, {
  title: "Water Systems",
  description: "Water heater repair, tankless installation, and water line replacement.",
  icon: Droplets
}, {
  title: "Drain & Sewer",
  description: "Main line cleaning, hydro-jetting, and drain camera inspection.",
  icon: Waves
}, {
  title: "Preventative Maintenance",
  description: "Seasonal pipe inspection, pressure testing, and non-invasive leak detection.",
  icon: ShieldCheck
}];
const pricing = [{
  service: "Leak Inspection",
  price: "Quote from your pro"
}, {
  service: "Drain Clearing (simple)",
  price: "Quote from your pro"
}, {
  service: "Emergency Callout",
  price: "Quote from your pro"
}, {
  service: "Water Heater Flush",
  price: "Quote from your pro"
}, {
  service: "Toilet Replacement",
  price: "Quote from your pro"
}, {
  service: "Pipe Repair (average)",
  price: "Quote from your pro"
}, {
  service: "Full Repiping",
  price: "Quote from your pro"
}];
const containerVariants = {
  hidden: {},
  show: {
    transition: {
      staggerChildren: 0.1
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: "easeOut"
    }
  }
};
export default function ServicePricing() {
  return <section id="servicepricing" className="bg-background py-16 md:py-24">
      <div className="container mx-auto px-4 md:px-6">
        <div className="mb-12 text-center md:mb-16">
          <motion.div initial={{
          opacity: 0,
          y: -20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="flex flex-col items-center justify-center space-y-4">
            <Badge variant="secondary" className="px-3 py-1 text-sm">
              Transparent Pricing
            </Badge>
            <h2 className="font-default text-3xl font-bold tracking-tight text-foreground sm:text-4xl md:text-5xl">
              Plumbing Services & Pricing
            </h2>
            <p className="max-w-[700px] text-muted-foreground md:text-lg">
              No hidden fees. Final estimate provided before any work begins. We specialize in fast diagnosis and lasting repairs.
            </p>
          </motion.div>
        </div>

        <div className="grid gap-8 lg:grid-cols-12 lg:gap-12">
          {}
          <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
          once: true
        }} className="grid gap-6 sm:grid-cols-2 lg:col-span-7">
            {categories.map((category, i) => {
            const Icon = category.icon;
            return <motion.div key={i} data-index={i} variants={itemVariants}>
                  <Card className="h-full border-border/50 bg-card/50 shadow-sm transition-shadow hover:shadow-md">
                    <CardHeader className="pb-4">
                      <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10 text-primary">
                        <Icon className="h-6 w-6" />
                      </div>
                      <CardTitle className="font-default text-xl">{category.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="text-base text-muted-foreground">
                        {category.description}
                      </CardDescription>
                    </CardContent>
                  </Card>
                </motion.div>;
          })}
          </motion.div>

          {}
          <motion.div initial={{
          opacity: 0,
          x: 20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="lg:col-span-5">
            <Card className="h-full border-primary/20 bg-card shadow-md">
              <CardHeader className="border-b border-border/50 bg-muted/30 pb-6">
                <CardTitle className="font-default text-2xl">Standard Rates</CardTitle>
                <CardDescription>
                  Average costs for common plumbing services in Atlanta.
                </CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow className="hover:bg-transparent">
                      <TableHead className="w-[60%] px-6 py-4 font-semibold text-foreground">Service</TableHead>
                      <TableHead className="px-6 py-4 text-right font-semibold text-foreground">Estimated Price</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pricing.map((item, i) => <TableRow key={i} data-index={i} className="border-border/50 transition-colors hover:bg-muted/50">
                        <TableCell className="px-6 py-4 font-medium text-foreground">
                          {item.service}
                        </TableCell>
                        <TableCell className="px-6 py-4 text-right text-muted-foreground">
                          {item.price}
                        </TableCell>
                      </TableRow>)}
                  </TableBody>
                </Table>
              </CardContent>
              <div className="border-t border-border/50 bg-muted/30 p-6">
                <ul className="mb-6 space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                    <span>Independent local plumbers screened through network onboarding</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                    <span>Warranty terms are set by the pro - confirm before work begins</span>
                  </li>
                </ul>
                <Button asChild className="w-full" size="lg">
                  <Link to="#bookingrequest">
                    Request a Quote
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </div>
            </Card>
          </motion.div>
        </div>
      </div>
    </section>;
}